/**
 * Which optional columns to hide so a `table-fixed` table fits its container.
 *
 * Every column but one has a fixed width; the one unsized column (the
 * "flexible" one) takes whatever is left. Hiding columns in `hideOrder` —
 * least important first — continues until the flexible column gets at least
 * `flexMinPx`, or nothing optional is left to hide (the table then scrolls
 * sideways instead; see `minTableWidthPx`).
 *
 * `width === 0` means "not measured yet" (and is what jsdom always reports),
 * so it hides nothing: guessing narrow would flash a cut-down table on every
 * load.
 */
export function hiddenColumnsForWidth(opts: {
  width: number;
  /** Fixed widths of the columns actually rendered, in px. */
  fixedPx: Record<string, number>;
  /** Hideable column ids, least important first. */
  hideOrder: readonly string[];
  flexMinPx: number;
}): Set<string> {
  const hidden = new Set<string>();
  if (opts.width === 0) return hidden;
  let used = Object.values(opts.fixedPx).reduce((a, b) => a + b, 0);
  for (const id of opts.hideOrder) {
    if (used + opts.flexMinPx <= opts.width) break;
    const px = opts.fixedPx[id];
    if (px === undefined) continue;
    hidden.add(id);
    used -= px;
  }
  return hidden;
}

/** The narrowest the table may get with `hidden` hidden: past this it scrolls. */
export function minTableWidthPx(opts: {
  fixedPx: Record<string, number>;
  hidden: ReadonlySet<string>;
  flexMinPx: number;
}): number {
  let used = opts.flexMinPx;
  for (const [id, px] of Object.entries(opts.fixedPx)) if (!opts.hidden.has(id)) used += px;
  return used;
}
